/**
 * A single clear, age-appropriate media invitation for each focused learning beat.
 * Only the two tested Master Teacher pilot Journeys opt in; the others are unchanged.
 * Pure presentation data: does not save progress, open assets, or modify gates.
 */
// Exact step-prefix allowlist for seven WonderLabs adventures. No legacy Summer Camp routes.
export const MASTER_TEACHER_STEP = /^(?:fdr-|wind-car-|flood-rescue-|rescue-prediction$|flat-bridge-test$|hidden-forces$|engineer-bridge-two$|fair-rescue-test$|young-engineer-report$|evidence-reflection$|next-variable-mission$|electric-spiral-|conductive-dough-|robot-artist-|explorers-robot-|magnetworks-)/;
const PILOT_STEP = MASTER_TEACHER_STEP;
const MEDIA_TYPES = new Set(["story_prelude", "local_video", "video_asset", "pwo_asset"]);

const clean = (value) => String(value || "").replace(/\s+/g, " ").trim();
const isVideo = (block) => block.type !== "pwo_asset";
const mediaLabel = (block) => clean(block.title || block.video?.title || "the science clue");
const cueButton = (block, stepId) => clean(block.master_teacher_watch?.button || getMasterTeacherWatchSpec(stepId, block)?.button) ||
  (block.type === "story_prelude" ? "Watch the Story" :
  block.type === "pwo_asset" ? "Open 3D Exploration" :
  block.type === "video_asset" ? "Open Video" : "Play the video");

/**
 * Default prominent, single-tap Watch action for self-guided local video/prelude.
 * Authored mission-specific metadata (where present) ALWAYS takes priority.
 * This is display-only; never makes watching a completion requirement.
 */
export function getMasterTeacherWatchSpec(stepId, block) {
  if (!MASTER_TEACHER_STEP.test(String(stepId || "")) || !block) return null;
  if (block.master_teacher_watch) return block.master_teacher_watch;
  if (block.type !== "local_video" && block.type !== "story_prelude" && block.type !== "video_asset") return null;
  const label = block.type === "story_prelude" ? "Watch the Story" : "Watch the Clue";
  return {
    button: label,
    why: block.type === "story_prelude"
      ? "Watch for the mystery you'll investigate."
      : "Look for a clue you can use in your next investigation.",
    after: "What did you notice? Carry that clue into your next investigation.",
  };
}

/** Called with the exact beat.blockIds, not every media block in a phase. */
export function getMasterTeacherMediaCue(stepId, blocks, beat) {
  if (!PILOT_STEP.test(String(stepId || "")) || !Array.isArray(beat?.blockIds)) return null;
  const byId = new Map((Array.isArray(blocks) ? blocks : []).map((block) => [block.id, block]));
  const media = beat.blockIds.map((id) => byId.get(id)).filter((block) =>
    block && MEDIA_TYPES.has(block.type) &&
    (block.type !== "story_prelude" || block.video?.path));
  if (!media.length) return null;

  const optional = /\boptional\b/i.test(clean(beat.title)) || Boolean(media[0].optional);
  const first = media[0];
  const button = cueButton(first, stepId);
  const name = mediaLabel(first);
  let spoken = isVideo(first)
    ? `${optional ? "If you'd like another clue, tap" : "Tap"} ${button} to play ${name}.`
    : `${optional ? "If you're curious, tap" : "Tap"} Open 3D Exploration to explore ${name}.`;
  let short = isVideo(first) ? `Watch ${name}` : `Explore ${name} in 3D`;
  if (media.length > 1) {
    const second = media[1];
    spoken += isVideo(second)
      ? ` Then tap ${cueButton(second, stepId)} to play ${mediaLabel(second)}.`
      : ` Then open the 3D guide for ${mediaLabel(second)}.`;
    short += isVideo(second) ? `, then watch ${mediaLabel(second)}` :
      `, then explore ${mediaLabel(second)}`;
    if (media.length > 2) spoken += " You can explore the other clues here afterward.";
  } else {
    const why = clean(first.master_teacher_watch?.why);
    spoken += why ? ` ${why}` : optional
      ? " This is an extra clue, so you can continue without opening it."
      : isVideo(first) ? " Look for a clue before moving on."
      : " Turn the model to look closely. Come back here when you're ready.";
  }
  // One cue per beat: when video and 3D share a part, avoid competing narration.
  return { spoken, short, count: media.length,
    media: media.map((block) => ({ block_id: block.id, type: block.type, title: mediaLabel(block) })) };
}
